<template>
  <div class="link-analysis-container">
    <CCard>
      <CCardHeader>
        <CRow>
          <CCol md="8">
            <strong>🔍 ابزار تحلیل پیشرفته لینک‌سازی و خوشه‌بندی موضوعی</strong>
          </CCol>
          <CCol md="4" class="text-left">
            <CBadge color="info" size="lg" v-if="results.length > 0">
              امتیاز سئوی داخلی: {{ internalSEOScore }} / 100
            </CBadge>
          </CCol>
        </CRow>
      </CCardHeader>

      <CCardBody>
        <CAlert color="info" v-if="loading">
          <CSpinner component="span" size="sm" aria-hidden="true" />
          در حال آنالیز عمیق محتوا و استخراج خوشه‌های موضوعی...
        </CAlert>

        <CRow class="mb-3">
          <CCol md="4">
            <CSelect
                label="نوع آنالیز"
                :options="analysisTypeOptions"
                v-model="analysisType"
            />
          </CCol>
          <CCol md="4">
            <CSelect
                label="عمق تحلیل خوشه‌ها"
                :options="clusterDepthOptions"
                v-model="clusterDepth"
            />
          </CCol>
          <CCol md="4">
            <CButton
                color="primary"
                @click="analyzeAll"
                :disabled="loading"
                block
            >
              <CIcon name="cil-magnifying-glass" />
              شروع آنالیز عمیق
            </CButton>
          </CCol>
        </CRow>

        <CTabs v-if="results.length > 0" active-tab="summary">
          <!-- تب خلاصه نتایج -->
          <CTab title="📊 خلاصه نتایج" tab="summary">
            <CCard class="mt-3">
              <CCardBody>
                <CRow>
                  <CCol md="3">
                    <CCard class="text-center bg-light">
                      <CCardBody>
                        <div class="text-value-lg text-primary">{{ totalProductLinks }}</div>
                        <div>لینک به محصولات</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="text-center bg-light">
                      <CCardBody>
                        <div class="text-value-lg text-success">{{ totalArticleLinks }}</div>
                        <div>لینک به مقالات</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="text-center bg-light">
                      <CCardBody>
                        <div class="text-value-lg text-warning">{{ totalErrors }}</div>
                        <div>خطاهای لینک‌دهی</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="text-center bg-light">
                      <CCardBody>
                        <div class="text-value-lg text-info">{{ totalClusters }}</div>
                        <div>خوشه‌های موضوعی</div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>

                <CRow class="mt-3">
                  <CCol md="6">
                    <CCard class="text-center">
                      <CCardBody>
                        <div class="text-value-lg">
                          <CBadge :color="getSEOScoreColor(internalSEOScore)" size="lg">
                            {{ internalSEOScore }}%
                          </CBadge>
                        </div>
                        <div>امتیاز سئوی داخلی</div>
                        <small class="text-muted">
                          بر اساس کیفیت لینک‌سازی، تنوع Anchor Text و پوشش خوشه‌ها
                        </small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="6">
                    <CCard class="text-center">
                      <CCardBody>
                        <div class="text-value-lg">{{ linkDensity }}</div>
                        <div>چگالی لینک‌ها (لینک به ازای هر ۱۰۰۰ کلمه)</div>
                        <small class="text-muted">
                          محدوده ایده‌آل: ۳-۵ لینک در هر ۱۰۰۰ کلمه
                        </small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CTab>

          <!-- تب خوشه‌های موضوعی -->
          <CTab title="🎯 خوشه‌های موضوعی" tab="clusters">
            <CCard class="mt-3">
              <CCardHeader>
                <CRow>
                  <CCol md="6">
                    <strong>ساختار سلسله‌مراتبی خوشه‌ها</strong>
                    <small class="text-muted d-block">H1 (عنوان صفحه) → H2 (خوشه‌های اصلی) → H3 (زیرخوشه‌ها)</small>
                  </CCol>
                  <CCol md="6" class="text-left">
                    <CBadge color="success">
                      {{ totalClusters }} خوشه اصلی
                    </CBadge>
                    <CBadge color="info" class="mr-1">
                      {{ totalSubClusters }} زیرخوشه
                    </CBadge>
                  </CCol>
                </CRow>
              </CCardHeader>
              <CCardBody>
                <div v-for="(cluster, index) in clusterAnalysis" :key="index" class="cluster-item">
                  <CCard class="mb-3" :class="{'border-primary': cluster.isPillar}">
                    <CCardHeader>
                      <CRow>
                        <CCol md="8">
                          <h5 class="mb-0">
                            <CIcon :name="cluster.isPillar ? 'cil-star' : 'cil-folder'" />
                            {{ cluster.title }}
                            <CBadge :color="cluster.isPillar ? 'danger' : 'info'" class="mr-2">
                              {{ cluster.isPillar ? '📌 خوشه اصلی (Pillar)' : 'خوشه فرعی' }}
                            </CBadge>
                            <CBadge :color="getClusterImportanceColor(cluster.importance)" class="mr-2">
                              {{ getClusterImportanceLabel(cluster.importance) }}
                            </CBadge>
                          </h5>
                        </CCol>
                        <CCol md="4" class="text-left">
                          <small class="text-muted">
                            {{ cluster.wordCount }} کلمه |
                            {{ cluster.linkCount }} لینک
                          </small>
                        </CCol>
                      </CRow>
                    </CCardHeader>
                    <CCardBody>
                      <div class="mb-2">
                        <strong>کلمات کلیدی خوشه:</strong>
                        <CBadge
                            v-for="(keyword, kidx) in cluster.keywords"
                            :key="kidx"
                            color="secondary"
                            class="mr-1"
                        >
                          {{ keyword }}
                        </CBadge>
                      </div>

                      <div v-if="cluster.suggestedLinks && cluster.suggestedLinks.length" class="mt-2">
                        <strong class="text-success">پیشنهاد لینک‌سازی:</strong>
                        <ul class="mt-1">
                          <li v-for="(link, lidx) in cluster.suggestedLinks" :key="lidx">
                            <span class="text-primary">{{ link.targetPage }}</span>
                            <small class="text-muted"> - {{ link.reason }}</small>
                            <CBadge :color="getPriorityColor(link.priority)" class="mr-1">
                              {{ getPriorityLabel(link.priority) }}
                            </CBadge>
                          </li>
                        </ul>
                      </div>

                      <!-- خوشه‌های فرعی (H2) -->
                      <div v-if="cluster.subClusters && cluster.subClusters.length" class="mt-3">
                        <strong>خوشه‌های فرعی (H2):</strong>
                        <div v-for="(sub, sidx) in cluster.subClusters" :key="sidx" class="sub-cluster mt-2">
                          <CCard>
                            <CCardBody>
                              <h6>
                                <CIcon name="cil-folder-open" />
                                {{ sub.title }}
                                <CBadge color="warning" class="mr-2">
                                  {{ sub.linkCount }} لینک
                                </CBadge>
                              </h6>
                              <small class="text-muted">{{ sub.wordCount }} کلمه</small>
                              <div>
                                <CBadge
                                    v-for="(kw, kwidx) in sub.keywords"
                                    :key="kwidx"
                                    color="light"
                                    class="mr-1"
                                >
                                  {{ kw }}
                                </CBadge>
                              </div>

                              <!-- زیرخوشه‌ها (H3) -->
                              <div v-if="sub.subClusters && sub.subClusters.length" class="mt-2">
                                <small><strong>زیرخوشه‌ها (H3):</strong></small>
                                <div v-for="(subsub, ssidx) in sub.subClusters" :key="ssidx" class="sub-sub-cluster mt-1">
                                  <small>
                                    <span class="text-muted">• {{ subsub.title }}</span>
                                    <span class="text-muted mr-1">({{ subsub.wordCount }} کلمه)</span>
                                    <CBadge
                                        v-for="(kw, kwidx) in subsub.keywords"
                                        :key="kwidx"
                                        color="secondary"
                                        size="sm"
                                        class="mr-1"
                                    >
                                      {{ kw }}
                                    </CBadge>
                                  </small>
                                </div>
                              </div>
                            </CCardBody>
                          </CCard>
                        </div>
                      </div>

                      <div class="mt-2">
                        <small>
                          <CBadge :color="cluster.coverage >= 70 ? 'success' : 'warning'">
                            پوشش لینک‌دهی: {{ cluster.coverage }}%
                          </CBadge>
                        </small>
                      </div>
                    </CCardBody>
                  </CCard>
                </div>
              </CCardBody>
            </CCard>
          </CTab>

          <!-- تب گزارش کلمات کلیدی -->
          <CTab title="🔑 کلمات کلیدی" tab="keywords">
            <CDataTable
                :items="keywordReport"
                :fields="keywordFields"
                items-per-page-select
                :items-per-page="10"
                hover
                pagination
                class="mt-3"
            >
              <template #count="{item}">
                <CBadge color="primary">{{ item.count }}</CBadge>
              </template>
              <template #type="{item}">
                <CBadge :color="item.type === 'product' ? 'info' : 'success'">
                  {{ item.type === 'product' ? 'محصول' : 'مقاله' }}
                </CBadge>
              </template>
              <template #density="{item}">
                <CProgress
                    :value="item.density"
                    :max="100"
                    color="info"
                    height="10px"
                />
                <small>{{ item.density }}%</small>
              </template>
            </CDataTable>
          </CTab>

          <!-- تب خطاها -->
          <CTab title="⚠️ خطاها" tab="errors">
            <CDataTable
                :items="allErrors"
                :fields="errorFields"
                items-per-page-select
                :items-per-page="10"
                hover
                pagination
                class="mt-3"
            >
              <template #message="{item}">
                <div class="error-container">
                  <div class="error-message">
                    <CIcon :name="getErrorIcon(item.severity)" :class="`text-${getSeverityColor(item.severity)}`" />
                    {{ item.message }}
                  </div>
                  <div v-if="item.suggestion" class="suggestion-box">
                    <CBadge color="info" class="mr-1">💡 پیشنهاد</CBadge>
                    <span class="text-muted">{{ item.suggestion }}</span>
                  </div>
                  <div v-if="item.anchorText || item.href" class="detail-box">
                    <small>
                      <span v-if="item.anchorText" class="badge badge-light">لنگر: "{{ item.anchorText }}"</span>
                      <span v-if="item.href" class="badge badge-light mr-1">URL: {{ item.href }}</span>
                      <span v-if="item.repetition" class="badge badge-warning mr-1">تکرار: {{ item.repetition }} بار</span>
                      <span v-if="item.length" class="badge badge-info mr-1">طول: {{ item.length }} کاراکتر</span>
                      <span v-if="item.anchorLength" class="badge badge-secondary mr-1">تعداد کلمات لنگر: {{ item.anchorLength }}</span>
                      <span v-if="item.targetLength" class="badge badge-secondary mr-1">کلمات کلیدی هدف: {{ item.targetLength }}</span>
                    </small>
                  </div>
                </div>
              </template>
              <template #severity="{item}">
                <CBadge :color="getSeverityColor(item.severity)">
                  {{ getSeverityLabel(item.severity) }}
                </CBadge>
              </template>
            </CDataTable>
          </CTab>

          <!-- تب پیشنهادات بهبود -->
          <CTab title="💡 پیشنهادات بهبود" tab="suggestions">
            <CCard class="mt-3">
              <CCardBody>
                <CListGroup>
                  <CListGroupItem
                      v-for="(suggestion, idx) in allSuggestions"
                      :key="idx"
                      class="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <CIcon :name="getSuggestionIcon(suggestion.type)" />
                      {{ suggestion.text }}
                    </div>
                    <CBadge :color="getSuggestionPriorityColor(suggestion.priority)">
                      {{ getSuggestionPriorityLabel(suggestion.priority) }}
                    </CBadge>
                  </CListGroupItem>
                </CListGroup>
              </CCardBody>
            </CCard>
          </CTab>
        </CTabs>

        <!-- نتایج جزئیات صفحات -->
        <div v-for="(result, index) in results" :key="index" class="mt-4">
          <CCard>
            <CCardHeader>
              <strong>{{ result.title }}</strong>
              <CBadge :color="result.type === 'product' ? 'info' : 'success'" class="mr-2">
                {{ result.type === 'product' ? 'محصول' : 'مقاله' }}
              </CBadge>
              <CBadge color="secondary">{{ result.url }}</CBadge>
              <CBadge color="primary" class="mr-2">
                {{ result.clusterCount || 0 }} خوشه
              </CBadge>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="2">
                  <strong>لینک به محصولات:</strong> {{ result.productLinksCount }}
                </CCol>
                <CCol md="2">
                  <strong>لینک به مقالات:</strong> {{ result.articleLinksCount }}
                </CCol>
                <CCol md="2">
                  <strong>کلمه کلیدی:</strong> {{ result.keyword }}
                </CCol>
                <CCol md="2">
                  <strong>تعداد کلمات:</strong> {{ result.totalWords }}
                </CCol>
                <CCol md="2">
                  <strong>چگالی لینک:</strong> {{ result.linkDensity }}
                </CCol>
                <CCol md="2">
                  <strong>خوشه‌ها:</strong> {{ result.clusterCount || 0 }}
                </CCol>
              </CRow>

              <div v-if="result.errors && result.errors.length">
                <h6 class="mt-3 text-danger">خطاها:</h6>
                <CAlert color="warning" v-for="(error, i) in result.errors" :key="i">
                  <div>{{ error.message }}</div>
                  <div v-if="error.suggestion" class="mt-1">
                    <small class="text-success">💡 {{ error.suggestion }}</small>
                  </div>
                </CAlert>
              </div>
            </CCardBody>
          </CCard>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
export default {
  name: 'LinkAnalysis',
  props: {
    items: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      analysisType: 'all',
      clusterDepth: 'deep',
      analysisTypeOptions: [
        { value: 'all', label: 'همه موارد' },
        { value: 'articles', label: 'فقط مقالات' },
        { value: 'products', label: 'فقط محصولات' }
      ],
      clusterDepthOptions: [
        { value: 'shallow', label: 'فقط خوشه‌های اصلی' },
        { value: 'deep', label: 'خوشه‌های اصلی + زیرخوشه‌ها' },
        { value: 'detailed', label: 'تحلیل دقیق (H1 تا H6)' }
      ],
      results: [],
      keywordFields: [
        { key: 'keyword', label: 'کلمه کلیدی' },
        { key: 'count', label: 'تعداد لینک‌ها' },
        { key: 'type', label: 'نوع صفحه' },
        { key: 'density', label: 'چگالی' }
      ],
      errorFields: [
        { key: 'title', label: 'صفحه' },
        { key: 'message', label: 'پیغام خطا' },
        { key: 'severity', label: 'شدت' }
      ],
      stopWords: [
        'از', 'با', 'برای', 'در', 'به', 'بر', 'روی', 'زیر', 'کنار',
        'جلوی', 'پشت', 'داخل', 'خارج', 'بالای', 'پایین', 'نزدیک',
        'دور', 'بین', 'میان', 'طریق', 'وسیلۀ', 'توسط', 'بجز', 'غیر',
        'مثل', 'مانند', 'همانند', 'چون', 'چرا', 'زیرا', 'چراکه',
        'که', 'تا', 'مگر', 'آیا', 'چه', 'چگونه', 'چطور', 'کجا',
        'کی', 'چند', 'چقدر', 'کدام', 'کدامین', 'این', 'آن', 'اینها',
        'آنها', 'ما', 'شما', 'او', 'ایشان', 'خود', 'خودی', 'یک', 'دو',
        'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'صد', 'هزار',
        'و', 'یا', 'اما', 'پس', 'پس‌از', 'پیش', 'پیش‌از', 'بعد', 'بعداز'
      ]
    };
  },
  computed: {
    totalProductLinks() {
      return this.results.reduce((sum, result) => sum + result.productLinksCount, 0);
    },
    totalArticleLinks() {
      return this.results.reduce((sum, result) => sum + result.articleLinksCount, 0);
    },
    totalErrors() {
      return this.results.reduce((sum, result) => sum + (result.errors ? result.errors.length : 0), 0);
    },
    analyzedItems() {
      return this.results.length;
    },
    totalClusters() {
      return this.clusterAnalysis.length;
    },
    totalSubClusters() {
      return this.clusterAnalysis.reduce((sum, cluster) => sum + (cluster.subClusters ? cluster.subClusters.length : 0), 0);
    },
    keywordReport() {
      const report = {};

      this.results.forEach(result => {
        if (result.linkedKeywords) {
          result.linkedKeywords.forEach(link => {
            const key = `${link.keyword}-${link.type}`;
            if (!report[key]) {
              report[key] = {
                keyword: link.keyword,
                type: link.type,
                count: 0,
                density: 0
              };
            }
            report[key].count += link.count;
          });
        }
      });

      Object.values(report).forEach(item => {
        const wordCount = this.results.reduce((sum, r) => sum + (r.totalWords || 0), 0);
        item.density = wordCount > 0 ? Math.round((item.count / wordCount) * 1000 * 100) / 100 : 0;
      });

      return Object.values(report);
    },
    allErrors() {
      const errors = [];
      this.results.forEach(result => {
        if (result.errors && result.errors.length) {
          result.errors.forEach(error => {
            errors.push({
              title: result.title,
              message: error.message || error,
              severity: error.severity || 'medium',
              suggestion: error.suggestion || null,
              anchorText: error.anchorText || null,
              href: error.href || null,
              repetition: error.repetition || null,
              length: error.length || null,
              anchorLength: error.anchorLength || null,
              targetLength: error.targetLength || null
            });
          });
        }
      });
      return errors;
    },
    clusterAnalysis() {
      if (this.results.length === 0) return [];

      const allClusters = [];
      this.results.forEach(result => {
        const clusters = this.extractClusters(result.htmlContent || '', result.title);
        allClusters.push(...clusters);
      });
      return allClusters;
    },
    internalSEOScore() {
      if (this.results.length === 0) return 0;

      let score = 0;
      const totalResults = this.results.length;

      const linkScore = Math.min((this.totalProductLinks + this.totalArticleLinks) / totalResults * 5, 30);

      const uniqueKeywords = new Set();
      this.results.forEach(r => {
        if (r.linkedKeywords) {
          r.linkedKeywords.forEach(l => uniqueKeywords.add(l.keyword));
        }
      });
      const diversityScore = Math.min(uniqueKeywords.size * 2, 30);

      const clusterScore = Math.min(this.totalClusters * 3, 40);

      score = Math.round(linkScore + diversityScore + clusterScore);
      return Math.min(score, 100);
    },
    linkDensity() {
      if (this.results.length === 0) return 0;

      const totalWords = this.results.reduce((sum, r) => sum + (r.totalWords || 0), 0);
      const totalLinks = this.totalProductLinks + this.totalArticleLinks;

      return totalWords > 0 ? Math.round((totalLinks / totalWords) * 1000 * 100) / 100 : 0;
    },
    allSuggestions() {
      const suggestions = [];

      if (this.linkDensity < 2) {
        suggestions.push({
          text: 'چگالی لینک‌ها پایین است. سعی کنید به ازای هر ۱۰۰۰ کلمه، ۳-۵ لینک داخلی قرار دهید.',
          type: 'warning',
          priority: 'high'
        });
      } else if (this.linkDensity > 8) {
        suggestions.push({
          text: 'چگالی لینک‌ها زیاد است. ممکن است به عنوان لینک‌سازی اسپم در نظر گرفته شود.',
          type: 'danger',
          priority: 'high'
        });
      }

      this.clusterAnalysis.forEach(cluster => {
        if (cluster.linkCount === 0 && cluster.isPillar) {
          suggestions.push({
            text: `صفحه اصلی "${cluster.title}" هیچ لینکی ندارد. برای بهبود سئو، لینک‌های داخلی به این صفحه اضافه کنید.`,
            type: 'info',
            priority: 'high'
          });
        }
      });

      const anchors = [];
      this.results.forEach(r => {
        if (r.linkedKeywords) {
          r.linkedKeywords.forEach(l => {
            if (!anchors.includes(l.keyword)) {
              anchors.push(l.keyword);
            }
          });
        }
      });

      if (anchors.length < 3) {
        suggestions.push({
          text: 'تنوع Anchor Text پایین است. از کلمات کلیدی مختلف برای لینک‌دهی استفاده کنید.',
          type: 'warning',
          priority: 'medium'
        });
      }

      return suggestions;
    }
  },
  methods: {
    removeStopWords(text) {
      if (!text) return [];

      const words = text.split(/\s+/).filter(w => w.length > 0);

      return words.filter(word => {
        const cleanWord = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        return !this.stopWords.includes(cleanWord) && cleanWord.length > 0;
      });
    },

    countKeywords(text) {
      if (!text) return 0;
      const filteredWords = this.removeStopWords(text);
      return filteredWords.length;
    },

    extractKeywordsFromText(text) {
      if (!text) return [];

      const words = this.removeStopWords(text);
      const wordFreq = {};

      words.forEach(word => {
        const cleanWord = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        if (cleanWord.length > 2) {
          wordFreq[cleanWord] = (wordFreq[cleanWord] || 0) + 1;
        }
      });

      return Object.entries(wordFreq)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([word]) => word);
    },

    extractClusters(htmlContent, pageTitle) {
      if (!htmlContent) return [];

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const clusters = [];

      // H1 = pageTitle (از props)
      const h2Elements = doc.querySelectorAll('h2');

      // خوشه اصلی (Pillar) = title صفحه
      const mainCluster = {
        title: pageTitle || 'صفحه اصلی',
        isMain: true,
        isPillar: true,
        importance: 'high',
        wordCount: this.countKeywords(htmlContent),
        linkCount: doc.querySelectorAll('a').length,
        coverage: 0,
        keywords: this.extractKeywordsFromText(htmlContent),
        subClusters: [],
        suggestedLinks: []
      };

      // استخراج H2ها به عنوان خوشه‌های فرعی
      h2Elements.forEach((h2, index) => {
        const subCluster = {
          title: h2.textContent.trim(),
          isMain: false,
          isPillar: false,
          importance: 'medium',
          wordCount: 0,
          linkCount: 0,
          coverage: 0,
          keywords: [],
          subClusters: [],
          suggestedLinks: []
        };

        let contentBetween = '';
        let nextElement = h2.nextElementSibling;
        const nextH2 = h2Elements[index + 1];

        while (nextElement && nextElement !== nextH2) {
          contentBetween += nextElement.textContent || '';
          const linksInSection = nextElement.querySelectorAll('a').length;
          subCluster.linkCount += linksInSection;
          nextElement = nextElement.nextElementSibling;
        }

        subCluster.wordCount = this.countKeywords(contentBetween);
        subCluster.keywords = this.extractKeywordsFromText(contentBetween);

        // پیدا کردن H3ها به عنوان زیرخوشه
        let currentH3 = h2.nextElementSibling;
        while (currentH3 && currentH3 !== nextH2) {
          if (currentH3.tagName === 'H3') {
            const subSubCluster = {
              title: currentH3.textContent.trim(),
              wordCount: 0,
              keywords: []
            };

            let subContent = '';
            let nextElem = currentH3.nextElementSibling;
            while (nextElem && nextElem !== nextH2 && nextElem.tagName !== 'H3') {
              subContent += nextElem.textContent || '';
              nextElem = nextElem.nextElementSibling;
            }

            subSubCluster.wordCount = this.countKeywords(subContent);
            subSubCluster.keywords = this.extractKeywordsFromText(subContent);

            subCluster.subClusters.push(subSubCluster);
          }
          currentH3 = currentH3.nextElementSibling;
        }

        // محاسبه پوشش لینک‌دهی برای خوشه فرعی
        subCluster.coverage = Math.min(Math.round((subCluster.linkCount / Math.max(1, subCluster.subClusters.length)) * 20), 100);

        if (subCluster.linkCount === 0 && subCluster.subClusters.length > 0) {
          subCluster.suggestedLinks.push({
            targetPage: `مقاله مرتبط با "${subCluster.keywords[0] || subCluster.title}"`,
            reason: 'این خوشه هیچ لینکی ندارد. لینک‌دهی به مقالات مرتبط باعث بهبود سئو می‌شود.',
            priority: 'high'
          });
        }

        mainCluster.subClusters.push(subCluster);
      });

      // محاسبه پوشش کلی
      const totalSubClusters = mainCluster.subClusters.length;
      const linkedSubClusters = mainCluster.subClusters.filter(c => c.linkCount > 0).length;
      mainCluster.coverage = totalSubClusters > 0 ? Math.round((linkedSubClusters / totalSubClusters) * 100) : 0;

      if (mainCluster.linkCount === 0) {
        mainCluster.suggestedLinks.push({
          targetPage: `صفحات مرتبط با "${mainCluster.keywords[0] || mainCluster.title}"`,
          reason: 'صفحه اصلی هیچ لینکی ندارد. لینک‌دهی به صفحات مرتبط باعث بهبود سئو می‌شود.',
          priority: 'high'
        });
      }

      clusters.push(mainCluster);

      return clusters;
    },

    async analyzeAll() {
      this.loading = true;
      this.results = [];

      try {
        let itemsToAnalyze = this.items;
        if (this.analysisType === 'articles') {
          itemsToAnalyze = this.items.filter(item => item.type === 'article');
        } else if (this.analysisType === 'products') {
          itemsToAnalyze = this.items.filter(item => item.type === 'product');
        }

        for (const item of itemsToAnalyze) {
          const result = await this.analyzeItem(item);
          this.results.push(result);
        }
      } catch (error) {
        console.error('Error in analysis:', error);
      } finally {
        this.loading = false;
      }
    },

    analyzeItem(item) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(item.htmlContent || '', 'text/html');

          const textContent = doc.body.textContent || '';
          const totalWords = this.countKeywords(textContent);

          const result = {
            title: item.title,
            url: item.url,
            type: item.type,
            keyword: item.keyword,
            seo_title: item.seo_title || item.title,
            keywordWordCount: this.countKeywords(item.keyword),
            productLinksCount: 0,
            articleLinksCount: 0,
            errors: [],
            linkedKeywords: [],
            totalWords: totalWords,
            htmlContent: item.htmlContent,
            clusterCount: 0,
            linkDensity: 0
          };

          const links = doc.querySelectorAll('a');
          const anchorTexts = new Map();
          let hasKeywordLink = false;

          links.forEach(link => {
            const href = link.getAttribute('href');
            const anchorText = link.textContent.trim();

            if (!anchorText && href) {
              result.errors.push({
                message: `لینک به "${href}" دارای متن لنگر (Anchor Text) خالی است.`,
                severity: 'high',
                suggestion: 'برای بهبود سئو و دسترسی‌پذیری، به هر لینک یک متن معنادار اضافه کنید.',
                href: href
              });
              return;
            }

            const anchorWordCount = this.countKeywords(anchorText);
            if (anchorWordCount > 5) {
              result.errors.push({
                message: `لنگر "${anchorText}" بیش از حد طولانی است (${anchorWordCount} کلمه کلیدی).`,
                severity: 'low',
                suggestion: 'لنگرهای کوتاه‌تر و هدفمندتر (حداکثر ۳-۴ کلمه کلیدی) برای سئو بهتر هستند.',
                anchorText: anchorText,
                length: anchorWordCount
              });
            }

            if (anchorText) {
              if (anchorTexts.has(anchorText)) {
                anchorTexts.set(anchorText, anchorTexts.get(anchorText) + 1);
              } else {
                anchorTexts.set(anchorText, 1);
              }
            }

            if (href && href.includes('/')) {
              const isClusterLink = href.includes('/blog/') || href.includes('/article/');

              if (href.includes('/product')) {
                result.productLinksCount++;
                if (anchorText) {
                  this.trackKeyword(result, anchorText, 'product');
                }
              } else if (href.includes('/blog') || href.includes('/article')) {
                result.articleLinksCount++;
                if (anchorText) {
                  this.trackKeyword(result, anchorText, 'article');
                }

                if (isClusterLink && item.keyword) {
                  const cleanAnchor = this.removeStopWords(anchorText).join(' ');
                  const cleanKeyword = this.removeStopWords(item.keyword).join(' ');

                  const containsKeyword = cleanAnchor.includes(cleanKeyword) ||
                      cleanKeyword.includes(cleanAnchor);
                  const isExactMatch = cleanAnchor === cleanKeyword;

                  if (!containsKeyword) {
                    result.errors.push({
                      message: `لینک به مقاله با لنگر "${anchorText}" که شامل کلمه کلیدی اصلی "${item.keyword}" نیست.`,
                      severity: 'medium',
                      suggestion: `برای ارتباط بهتر موضوعی، از لنگرهایی استفاده کنید که شامل "${item.keyword}" باشند. مثال: "${item.keyword} حرفه‌ای" یا "خدمات ${item.keyword}"`,
                      anchorText: anchorText,
                      targetKeyword: item.keyword
                    });
                  } else if (isExactMatch && result.articleLinksCount > 1) {
                    result.errors.push({
                      message: `لنگر دقیق "${item.keyword}" بیش از یک بار تکرار شده است.`,
                      severity: 'low',
                      suggestion: `برای تنوع، از لنگرهای مشابه استفاده کنید: "خدمات ${item.keyword}"، "مشاوره ${item.keyword}"، "اصول ${item.keyword}"`,
                      anchorText: anchorText,
                      repetition: result.articleLinksCount
                    });
                  } else if (containsKeyword && !isExactMatch) {
                    hasKeywordLink = true;
                  }
                }

                if (item.keyword && anchorText) {
                  const itemKeywordCount = this.countKeywords(item.keyword);
                  const anchorKeywordCount = this.countKeywords(anchorText);

                  if (Math.abs(itemKeywordCount - anchorKeywordCount) > 2) {
                    let errorMessage = '';
                    let suggestion = '';

                    if (anchorKeywordCount > itemKeywordCount) {
                      errorMessage = `لنگر لینک "${anchorText}" دارای ${anchorKeywordCount} کلمه کلیدی است (${anchorKeywordCount - itemKeywordCount} کلمه بیشتر از کلمه کلیدی اصلی "${item.keyword}")`;
                      suggestion = `پیشنهاد: از لنگر "${item.keyword}" یا ترکیبی با ${itemKeywordCount} کلمه کلیدی استفاده کنید.`;
                    } else {
                      errorMessage = `لنگر لینک "${anchorText}" دارای ${anchorKeywordCount} کلمه کلیدی است (${itemKeywordCount - anchorKeywordCount} کلمه کمتر از کلمه کلیدی اصلی "${item.keyword}")`;
                      suggestion = `پیشنهاد: از لنگر "${item.keyword} در اصفهان" یا "${item.keyword} حرفه‌ای" با ${itemKeywordCount + 1} کلمه کلیدی استفاده کنید.`;
                    }

                    result.errors.push({
                      message: errorMessage,
                      severity: 'medium',
                      suggestion: suggestion,
                      anchorText: anchorText,
                      anchorLength: anchorKeywordCount,
                      targetKeyword: item.keyword,
                      targetLength: itemKeywordCount
                    });
                  }
                }
              }
            }

            if (href && href.length > 80) {
              result.errors.push({
                message: `URL "${href}" بسیار طولانی است (${href.length} کاراکتر).`,
                severity: 'medium',
                suggestion: 'از URLهای کوتاه و خوانا برای صفحات استفاده کنید.',
                href: href,
                length: href.length
              });
            }
          });

          anchorTexts.forEach((count, text) => {
            if (count > 2) {
              result.errors.push({
                message: `لنگر "${text}" ${count} بار تکرار شده است.`,
                severity: 'low',
                suggestion: 'از لنگرهای متنوع برای لینک‌دهی به صفحات مختلف استفاده کنید.',
                anchorText: text,
                repetition: count
              });
            }
          });

          const clusters = this.extractClusters(item.htmlContent || '', item.title);
          result.clusterCount = clusters.length;

          result.linkDensity = totalWords > 0 ? Math.round(((result.productLinksCount + result.articleLinksCount) / totalWords) * 1000 * 100) / 100 : 0;

          resolve(result);
        }, 200);
      });
    },

    trackKeyword(result, keyword, type) {
      const existing = result.linkedKeywords.find(k => k.keyword === keyword && k.type === type);
      if (existing) {
        existing.count++;
      } else {
        result.linkedKeywords.push({
          keyword,
          type,
          count: 1
        });
      }
    },

    getSEOScoreColor(score) {
      if (score >= 80) return 'success';
      if (score >= 60) return 'warning';
      return 'danger';
    },

    getClusterImportanceColor(importance) {
      const colors = {
        high: 'danger',
        medium: 'warning',
        low: 'info'
      };
      return colors[importance] || 'secondary';
    },

    getClusterImportanceLabel(importance) {
      const labels = {
        high: 'بسیار مهم',
        medium: 'متوسط',
        low: 'کم اهمیت'
      };
      return labels[importance] || importance;
    },

    getPriorityColor(priority) {
      const colors = {
        high: 'danger',
        medium: 'warning',
        low: 'info'
      };
      return colors[priority] || 'secondary';
    },

    getPriorityLabel(priority) {
      const labels = {
        high: 'اولویت بالا',
        medium: 'اولویت متوسط',
        low: 'اولویت پایین'
      };
      return labels[priority] || priority;
    },

    getSeverityColor(severity) {
      const colors = {
        high: 'danger',
        medium: 'warning',
        low: 'info'
      };
      return colors[severity] || 'secondary';
    },

    getSeverityLabel(severity) {
      const labels = {
        high: 'شدید',
        medium: 'متوسط',
        low: 'کم'
      };
      return labels[severity] || severity;
    },

    getErrorIcon(severity) {
      const icons = {
        high: 'cil-warning',
        medium: 'cil-info',
        low: 'cil-check'
      };
      return icons[severity] || 'cil-info';
    },

    getSuggestionIcon(type) {
      const icons = {
        warning: 'cil-warning',
        danger: 'cil-ban',
        info: 'cil-info',
        success: 'cil-check'
      };
      return icons[type] || 'cil-info';
    },

    getSuggestionPriorityColor(priority) {
      const colors = {
        high: 'danger',
        medium: 'warning',
        low: 'info'
      };
      return colors[priority] || 'secondary';
    },

    getSuggestionPriorityLabel(priority) {
      const labels = {
        high: 'فوری',
        medium: 'مهم',
        low: 'توصیه‌شده'
      };
      return labels[priority] || priority;
    }
  }
};
</script>

<style scoped>
.link-analysis-container {
  direction: rtl;
  text-align: right;
}

.cluster-item {
  margin-bottom: 1rem;
}

.sub-cluster {
  margin-right: 2rem;
}

.sub-sub-cluster {
  margin-right: 3rem;
}

.text-value-lg {
  font-size: 2rem;
  font-weight: bold;
}

.border-primary {
  border: 2px solid #0d6efd !important;
}

.mr-1 {
  margin-left: 0.25rem !important;
}

.mr-2 {
  margin-left: 0.5rem !important;
}

.text-left {
  text-align: left !important;
}

.error-container {
  padding: 4px 0;
}

.error-message {
  font-weight: 500;
  margin-bottom: 4px;
}

.suggestion-box {
  background-color: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
  border-right: 3px solid #0d6efd;
  margin: 4px 0;
}

.detail-box {
  margin-top: 4px;
  padding: 4px 8px;
}

.badge-light {
  background-color: #e9ecef;
  color: #495057;
  padding: 4px 8px;
  margin-left: 4px;
}

.text-warning {
  color: #ffc107 !important;
}
</style>